/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { AppRoutes } from "../../routes/routes"
import { UserRole } from "../user/user-types"

/**
 * Roles that can access console pages.
 *
 * `ROLE_DEVICE_API` is excluded because it authenticates SDK requests, not
 * console users.
 */
// biome-ignore format: one item per line
const allAccessRoles: readonly UserRole[] = [
    UserRole.ROLE_CONSOLE_API_ADMIN,
    UserRole.ROLE_CONSOLE_API_ORGANIZATION_ADMIN,
    UserRole.ROLE_CONSOLE_API,
    UserRole.ROLE_CONSOLE_API_DEVELOPER,
]

/**
 * Roles that can manage users, organizations, and application types.
 */
// biome-ignore format: one item per line
const adminAccessRoles: readonly UserRole[] = [
    UserRole.ROLE_CONSOLE_API_ADMIN,
    UserRole.ROLE_CONSOLE_API_ORGANIZATION_ADMIN,
]

/**
 * Roles that can access developer tools such as Get SDK.
 */
// biome-ignore format: one item per line
const developerAccessRoles: readonly UserRole[] = [
    UserRole.ROLE_CONSOLE_API_ADMIN,
    UserRole.ROLE_CONSOLE_API_DEVELOPER
]

/**
 * Checks whether the user is a super admin with access across organizations.
 *
 * @param roles - Roles of the signed-in user.
 * @returns `true` when the roles include `ROLE_CONSOLE_API_ADMIN`.
 */
export const hasSuperAdminAccess = (roles: UserRole[]): boolean => roles.includes(UserRole.ROLE_CONSOLE_API_ADMIN)

/**
 * Checks whether the user has either admin role.
 *
 * @param roles - Roles of the signed-in user.
 * @returns `true` when the roles include a super admin or organization admin role.
 */
export const hasAdminAccess = (roles: UserRole[]): boolean => roles.some((role) => adminAccessRoles.includes(role))

/**
 * Checks whether the user is an organization admin.
 *
 * A super admin does not match unless they also hold the organization admin role.
 *
 * @param roles - Roles of the signed-in user.
 * @returns `true` when the roles include `ROLE_CONSOLE_API_ORGANIZATION_ADMIN`.
 */
export const hasOrgAdminAccess = (roles: UserRole[]): boolean =>
    roles.includes(UserRole.ROLE_CONSOLE_API_ORGANIZATION_ADMIN)

/**
 * Checks whether the user can access developer tools.
 *
 * These roles match the access rules for the Get SDK route.
 *
 * @param roles - Roles of the signed-in user.
 * @returns `true` when the roles include a developer or super admin role.
 */
export const hasDeveloperAccess = (roles: UserRole[]): boolean =>
    roles.some((role) => developerAccessRoles.includes(role))

/**
 * Roles each user role may grant to another user.
 *
 * This mirrors `Role.canManage` in `in-app-protection-core`. Keep it in sync
 * with the backend: roles not listed here cannot be granted. Role removal is
 * checked separately by the backend.
 */
const manageableRoles: Record<UserRole, readonly UserRole[]> = {
    [UserRole.ROLE_CONSOLE_API_ADMIN]: [
        UserRole.ROLE_CONSOLE_API_ADMIN,
        UserRole.ROLE_CONSOLE_API_ORGANIZATION_ADMIN,
        UserRole.ROLE_CONSOLE_API,
        UserRole.ROLE_DEVICE_API,
        UserRole.ROLE_CONSOLE_API_DEVELOPER
    ],
    [UserRole.ROLE_CONSOLE_API_ORGANIZATION_ADMIN]: [
        UserRole.ROLE_CONSOLE_API,
        UserRole.ROLE_DEVICE_API,
        UserRole.ROLE_CONSOLE_API_DEVELOPER
    ],
    [UserRole.ROLE_CONSOLE_API]: [],
    [UserRole.ROLE_DEVICE_API]: [],
    [UserRole.ROLE_CONSOLE_API_DEVELOPER]: []
}

/**
 * Checks whether any of the user's roles can grant a given role.
 *
 * @param roles - Roles of the user granting the role.
 * @param role - Role to grant.
 * @returns `true` when at least one user role may grant `role`.
 */
export const canManageRole = (roles: UserRole[], role: UserRole): boolean =>
    roles.some((ownRole) => manageableRoles[ownRole].includes(role))

/**
 * Returns the roles allowed to open a route.
 *
 * This controls client-side navigation only. The backend still authorizes every
 * API request independently.
 *
 * The switch is exhaustive so adding a route to `AppRoutes` requires an
 * explicit access rule.
 *
 * @param route - Route to look up.
 * @returns The roles allowed to open the route.
 *
 * @example
 * rolesForRoute(AppRoutes.GetSDK) // [ROLE_CONSOLE_API_ADMIN, ROLE_CONSOLE_API_DEVELOPER]
 */
export function rolesForRoute(route: AppRoutes): readonly UserRole[] {
    switch (route) {
        case AppRoutes.Home:
        case AppRoutes.Dashboard:
        case AppRoutes.Login:
        case AppRoutes.Settings:
        case AppRoutes.Logs:
        case AppRoutes.AccessDenied:
        case AppRoutes.PageNotFound:
        case AppRoutes.ResetPassword:
            return allAccessRoles

        // Developer tools
        case AppRoutes.GetSDK:
            return developerAccessRoles

        // Users
        case AppRoutes.AllUsers:
        case AppRoutes.UserDetail:
            return adminAccessRoles

        // Organizations: only a super admin can access the organization list.
        case AppRoutes.AllOrganizations:
            return [UserRole.ROLE_CONSOLE_API_ADMIN]
        case AppRoutes.Organization:
        case AppRoutes.OrganizationDetail:
            return adminAccessRoles

        // Application Types
        case AppRoutes.AllApplicationTypes:
        case AppRoutes.ApplicationTypeCreate:
        case AppRoutes.ApplicationTypeDetail:
            return adminAccessRoles

        // Applications
        case AppRoutes.Application:
        case AppRoutes.ApplicationDetail:
        case AppRoutes.ApplicationSearch:
            return allAccessRoles

        // APK details are reached from device details, so use the same access scope.
        case AppRoutes.ApkDetail:
            return allAccessRoles

        default: {
            // Keep this exhaustive: adding a route without an access rule must fail to compile.
            const unhandled: never = route
            throw new Error(`No roles defined for route ${unhandled}`)
        }
    }
}
